"use client";

import Link from "next/link";
import Script from "next/script";
import { useEffect, useMemo, useRef, useState } from "react";
import { useContent } from "@/components/shell/ContentContext";
import { useShell } from "@/components/shell/ShellContext";
import { SectionError } from "@/components/ui/states";
import { Icon } from "@/components/ui/Icon";
import { PlatformError } from "@/lib/api/client";
import { useSportsbookLaunch } from "@/lib/api/hooks";
import { messageFor } from "@/lib/api/messages";
import { SPORTSBOOK_ROOT, isSingleRowNav, usesTileNav } from "@/lib/site";
import { BETBY_THEMES_BASE, DEFAULT_THEME_NAME, NORTHSTAR_THEMES_BASE } from "@/lib/sportsbook/theme";
import type { SportsbookConfig } from "@/lib/content/types";

/* The sportsbook.

   Unlike a casino game, there is no URL to drop in an iframe: BetBy ships a
   renderer script that builds the whole book — sports tree, fixtures, prices,
   betslip — inside a container we hand it. So this component's job is to load
   that script, start the renderer with what `/sportsbook` gave us, and hand
   its callbacks back to our own shell so signing in, depositing and the
   balance behave like the rest of the site.

   The renderer owns every route under `SPORTSBOOK_ROOT`. It changes the URL
   itself, which is why the route is a catch-all and why the shell treats the
   whole subtree as one page — a remount would throw away the book and any
   betslip in it. */

const RENDERER_SCRIPT = "https://ui.invisiblesport.com/bt-renderer.min.js";

/* The element the renderer builds into. It attaches a shadow root to it, so
   nothing inside can be reached by our stylesheets — the colours come from the
   theme document instead (`src/lib/sportsbook/theme.ts`). The id is here to
   give that subtree a name when someone inspects the page. */
const CONTAINER_ID = "sportsbook";

/* Our chrome, in pixels, so the betslip docks against it instead of under it.
   The header is sticky at every width; the tab bar only exists below `lg`. */
const HEADER_HEIGHT_MOBILE = 56;
const HEADER_HEIGHT_LG = 80;
const TILE_NAV_HEIGHT = 131;
/* The Tabs layout's one row: 40px tabs and 12px above and below. */
const SINGLE_ROW_NAV_HEIGHT = 64;
const MOBILE_TAB_BAR_HEIGHT = 62;
const CHAT_RAIL_WIDTH = 256;
const LG_BREAKPOINT = "(min-width: 1024px)";

/* The tab bar is `62px + env(safe-area-inset-bottom)`, and the renderer takes a
   number rather than a CSS length — so the inset has to be measured. A probe
   reads the same value the tab bar does, which keeps the betslip off the home
   indicator without reaching into another component's DOM. */
const safeAreaBottom = () => {
  const probe = document.createElement("div");

  probe.style.cssText =
    "position:fixed;bottom:0;left:0;width:0;visibility:hidden;height:env(safe-area-inset-bottom)";
  document.body.append(probe);

  const inset = probe.getBoundingClientRect().height;

  probe.remove();

  return Math.round(inset);
};

type RendererOptions = {
  brand_id: string;
  token: string | null;
  themeName: string;
  lang: string;
  target: HTMLElement;
  basename: string;
  url?: string;
  stickyTop?: number;
  betSlipOffsetTop?: number;
  betSlipOffsetBottom?: number;
  betSlipOffsetRight?: number;
  themesBaseUrl?: string;
  betslipZIndex?: number;
  onTokenExpired?: () => Promise<string | null>;
  onLogin?: () => void;
  onRegister?: () => void;
  onRecharge?: () => void;
  onSessionRefresh?: () => void;
};

type RendererInstance = {
  kill: () => void;
  updateOptions: (options: Partial<RendererOptions>) => void;
};

declare global {
  interface Window {
    BTRenderer?: new () => { initialize: (options: RendererOptions) => RendererInstance };
  }
}

/* The betslip is a fixed overlay the renderer positions for us, so it has to
   be told how much of the viewport our own chrome already owns. */
const useChromeOffsets = (chatVisible: boolean, tileNavHeight: number) => {
  const [bottom, setBottom] = useState(0);
  const [top, setTop] = useState(HEADER_HEIGHT_LG);

  useEffect(() => {
    const query = window.matchMedia(LG_BREAKPOINT);
    const apply = () => {
      setBottom(query.matches ? 0 : MOBILE_TAB_BAR_HEIGHT + safeAreaBottom());
      setTop(
        query.matches
          ? HEADER_HEIGHT_LG + tileNavHeight
          : HEADER_HEIGHT_MOBILE,
      );
    };

    apply();
    query.addEventListener("change", apply);

    const ro = new ResizeObserver(apply);
    ro.observe(document.documentElement);

    return () => {
      query.removeEventListener("change", apply);
      ro.disconnect();
    };
  }, [tileNavHeight]);

  /* Memoised on the one value that actually varies, so the effect that pushes
     these into the renderer fires on a breakpoint change and not on every
     render. */
  return useMemo(
    () => ({
      stickyTop: top,
      betSlipOffsetTop: top,
      betSlipOffsetBottom: bottom,
      betSlipOffsetRight: chatVisible ? CHAT_RAIL_WIDTH : 0,
    }),
    [top, bottom, chatVisible],
  );
};

export function Sportsbook({
  config,
  siteBankrollId,
}: {
  config: SportsbookConfig;
  siteBankrollId: string;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const instanceRef = useRef<RendererInstance | null>(null);
  const [script, setScript] = useState<"pending" | "ready" | "error">("pending");
  /* Whether a book has ever been built. Once one is on screen it stays, even if
     a later call fails — see the error branch below. */
  const [started, setStarted] = useState(false);
  const { chatVisible, openSignIn, openWallet } = useShell();
  const { flags } = useContent();
  const tileLayout = usesTileNav(flags.navLayout);

  const { launch, error, isPending, refetch, refresh } = useSportsbookLaunch({
    siteBankrollId,
    lang: config.lang,
    oddsFormat: config.oddsFormat,
  });

  const offsets = useChromeOffsets(chatVisible, tileLayout ? (isSingleRowNav(flags.navLayout) ? SINGLE_ROW_NAV_HEIGHT : TILE_NAV_HEIGHT) : 0);

  /* Read through a ref inside the effect so a resize repositions the betslip
     without tearing the renderer down and rebuilding it. */
  const offsetsRef = useRef(offsets);
  offsetsRef.current = offsets;

  const brandId = launch?.brandId;
  /* A token arriving is a real change — the renderer has to restart to pick up
     the session. A token merely being replaced is not, and `refresh` handles
     that in place. */
  const hasSession = launch !== null && launch.token !== null;

  useEffect(() => {
    const container = containerRef.current;
    const Renderer = window.BTRenderer;

    if (script !== "ready" || !launch || brandId === undefined || !container || !Renderer) {
      return;
    }

    let cancelled = false;

    const start = (current: { token: string | null; brandId: string }) => {
      const instance = new Renderer().initialize({
        brand_id: current.brandId,
        token: current.token,
        themeName: config.themeName || DEFAULT_THEME_NAME,
        /* Where the renderer fetches the theme document. Ours repaints BetBy's
           in the design system's colours; theirs is the untouched original. */
        themesBaseUrl: config.themed ? NORTHSTAR_THEMES_BASE : BETBY_THEMES_BASE,
        lang: config.lang,
        target: container,
        basename: SPORTSBOOK_ROOT,
        ...offsetsRef.current,
        betslipZIndex: 30,
        /* Only meaningful with a session to expire. */
        ...(current.token === null
          ? {}
          : { onTokenExpired: async () => (await refresh()).token }),
        onLogin: () => openSignIn(),
        onRegister: () => openSignIn(),
        onRecharge: () => openWallet("Deposit"),
        /* The renderer asks for a whole new session rather than a new token,
           so the only honest answer is to build it again from a fresh launch. */
        onSessionRefresh: () => {
          refresh()
            .then((refreshed) => {
              if (cancelled || instanceRef.current !== instance) {
                return;
              }

              instance.kill();
              start(refreshed);
            })
            .catch(() => {
              /* Leave the current book standing. It still renders prices; only
                 placing a bet will fail, and the renderer says so itself. */
            });
        },
      });

      instanceRef.current = instance;
      setStarted(true);
    };

    start(launch);

    return () => {
      cancelled = true;
      instanceRef.current?.kill();
      instanceRef.current = null;
      setStarted(false);
    };
    /* `launch` is deliberately absent: it changes identity on every refetch,
       and restarting the book on a poll would drop the visitor's betslip. */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    script,
    brandId,
    hasSession,
    siteBankrollId,
    config.themeName,
    config.themed,
    config.lang,
    refresh,
    openSignIn,
    openWallet,
  ]);

  /* A resize only moves the betslip. */
  useEffect(() => {
    instanceRef.current?.updateOptions(offsets);
  }, [offsets]);

  if (error instanceof PlatformError && error.kind === "forbidden" && !started) {
    return <Unavailable />;
  }

  /* Only replace the page when there is nothing to replace. Once a book is up,
     a failed call — a session refresh that did not come back, most likely —
     leaves it standing: it still renders prices, and the renderer reports the
     failure itself when someone tries to bet. Swapping in an error panel would
     throw away a working book and whatever was in the visitor's betslip. */
  if ((script === "error" || error) && !started) {
    return (
      <div className="rounded-xl bg-elevation-1">
        <SectionError
          title={script === "error" ? "The sportsbook didn’t load" : "The sportsbook didn’t start"}
          body={
            script === "error"
              ? "The betting provider’s script could not be fetched. Reloading the page usually fixes it."
              : messageFor(error as PlatformError, "sportsbook")
          }
          action={
            <button
              type="button"
              onClick={() => {
                if (script === "error") {
                  window.location.reload();
                  return;
                }

                refetch();
              }}
              className="h-[40px] rounded-lg bg-elevation-2 px-[16px] text-[12px] font-bold leading-[1.6] transition-colors hover:bg-elevation-3"
            >
              Try again
            </button>
          }
        />
      </div>
    );
  }

  return (
    <>
      {!isPending && (
        <Script
          src={RENDERER_SCRIPT}
          strategy="afterInteractive"
          onReady={() => setScript("ready")}
          onError={() => setScript("error")}
        />
      )}

      {/* `isolate` is load-bearing. The renderer's own sticky rail and betslip
          carry z-indices well above our header's, and once the page scrolls far
          enough they paint straight over it — the menu button disappears under
          BetBy's sport icons. A stacking context here contains every z-index
          inside the shadow root, so the header, the chat rail and the modals
          all stay on top whatever the renderer does. It does not move anything:
          `isolation` is not one of the properties that re-parents a fixed
          element, so the betslip still docks to the viewport.

          The container itself is handed to the renderer, so nothing else may
          write to it. The placeholder sits alongside and goes when it paints. */}
      <div className="relative isolate min-h-[60dvh] w-full">
        <div ref={containerRef} id={CONTAINER_ID} className="w-full" />
        {(isPending || script !== "ready") && <Loading />}
      </div>
    </>
  );
}

function Loading() {
  return (
    <div className="pointer-events-none absolute inset-0 flex flex-col gap-3">
      <div className="h-[48px] animate-pulse rounded-lg bg-elevation-1" />
      <div className="h-[420px] animate-pulse rounded-xl bg-elevation-1" />
    </div>
  );
}

/* Geoblocking is an answer, not a fault: the API refuses to mint a launch for
   an address BetBy is not licensed in. Say that plainly, and point at the part
   of the site that does work here. */
function Unavailable() {
  return (
    <div className="grid place-items-center rounded-xl bg-elevation-1 px-[24px] py-[80px] text-center">
      <div className="flex max-w-[46ch] flex-col items-center gap-4">
        <span className="grid h-12 w-12 place-items-center rounded-full bg-elevation-2 text-fg-subtle">
          <Icon name="lock" size={20} />
        </span>
        <h2 className="text-h5 font-extrabold">
          Sports betting isn’t available here
        </h2>
        <p className="text-[12px] leading-[1.6] text-fg-subtle">
          Our sportsbook is not licensed in your region, so we can’t take a bet from you. What other
          players have settled is still below, and the casino is unaffected.
        </p>
        <Link
          href="/c/all"
          className="flex h-[44px] items-center gap-[8px] rounded-lg bg-brand px-[20px] text-[14px] font-bold leading-[1.6] text-fg transition-transform hover:-translate-y-0.5"
        >
          <Icon name="spade" size={15} />
          Browse the casino
        </Link>
      </div>
    </div>
  );
}
